/**
 * The drift summary's COUNTS line — the first line of every drifted
 * `statusReason` — as ONE vocabulary its producer formats through and its
 * readers parse through.
 *
 * WHY ONE DEFINITION. The line is written by the backend's drift fold and read,
 * in this package, by the roster classifier that decides whether a `DEGRADED`
 * cluster is a routine pending diff or a fault — and that decision is what the
 * `cluster-degraded` page keys on. When the two halves were two hand-spelled
 * grammars, the fold learned a clause (`server: raft roll pending`) the reader
 * did not know, every clause it does not know reads as a fault, and a routine
 * raft roll paged production. So the clause set is a closed union here: the
 * producer can only emit what `formatDriftCountsClause` spells, the reader can
 * only accept what `parseDriftCountsClause` reads, the two are pinned to each
 * other by a round trip, and `isRoutineDriftCountsClause` is an exhaustive switch
 * — a new clause kind is a compile error until somebody says whether it pages.
 *
 * WHY A LINE AND NOT A COLUMN. The counts line is the one carrier every reader of
 * `statusReason` already scans (`drift-detail-coverage-line.ts` makes the same
 * argument for its own line). Persisting the clauses structurally would be a
 * second fact to keep in agreement with this one.
 */

/** Every op a stack preview can count, in the order a counts clause lists them. */
export const DRIFT_PENDING_OPS = ["create", "update", "delete", "replace", "adopt"] as const;

export type DriftPendingOp = (typeof DRIFT_PENDING_OPS)[number];

/**
 * Whether each op, on its own, asks an operator to do anything. `delete` and
 * `replace` tear a live resource down, so a preview carrying one keeps the
 * caution chip even though it is "only" a pending diff; create, update and
 * adopt are what every backend deploy produces.
 */
const ROUTINE_PENDING_OP: Readonly<Record<DriftPendingOp, boolean>> = {
  create: true,
  update: true,
  delete: false,
  replace: false,
  adopt: true
};

/** One `N to <op>` phrase of a stack's counts. */
export interface DriftPendingCount {
  readonly count: number;
  readonly op: DriftPendingOp;
}

/**
 * The state a raft-tier roll is in when the drift preview finds work left on it.
 *
 * - `pending` — the converge's receiver would raise the surge and roll the tier.
 *   The server/vault twin of a node-pool launch-template update, which previews
 *   as `N to update` and is routine; it just cannot appear in a preview.
 * - `blocked` — the receiver would refuse (the group's max leaves no surge slot),
 *   so no converge will ever apply it. An operator has to act.
 */
export const RAFT_ROLL_CLAUSE_STATES = ["pending", "blocked"] as const;

export type RaftRollClauseState = (typeof RAFT_ROLL_CLAUSE_STATES)[number];

/** One `; `-separated clause of the counts line. */
export type DriftCountsClause =
  /** `<stack>: N to <op>[, N to <op>]*` — one drifted stack's preview counts. */
  | {
      readonly kind: "stack-counts";
      readonly stack: string;
      readonly counts: readonly DriftPendingCount[];
    }
  /** `missing stacks: <stack>[, <stack>]*` — stacks that should exist and are gone. */
  | { readonly kind: "missing-stacks"; readonly stacks: readonly string[] }
  /** `<pool>: raft roll <state>` — a raft-tier roll no stack preview can show. */
  | { readonly kind: "raft-roll"; readonly pool: string; readonly state: RaftRollClauseState };

export const DRIFT_COUNTS_CLAUSE_SEPARATOR = "; ";

const COUNT_SEPARATOR = ", ";
const MISSING_STACKS_PREFIX = "missing stacks: ";
const RAFT_ROLL_INFIX = ": raft roll ";

/**
 * A stack or pool name: lowercase, digits and dashes. The slot rejects a space,
 * which is what keeps `missing stacks: …` and a converge fault's free text from
 * ever reading as a name.
 */
const NAME = "[a-z][a-z0-9-]*";
const OP = DRIFT_PENDING_OPS.join("|");

/**
 * Anchored end to end on purpose. A converge FAULT writes the same
 * `<resource>: <text>` shape with a Pulumi error in the tail, and a partially
 * anchored pattern would read the head of one as a pending diff and silence a
 * real failure.
 */
const STACK_COUNTS_CLAUSE = new RegExp(`^(${NAME}): (\\d+ to (?:${OP})(?:, \\d+ to (?:${OP}))*)$`);
const PENDING_COUNT = new RegExp(`^(\\d+) to (${OP})$`);
const MISSING_STACKS_CLAUSE = new RegExp(`^missing stacks: (${NAME}(?:, ${NAME})*)$`);
const RAFT_ROLL_CLAUSE = new RegExp(
  `^(${NAME}): raft roll (${RAFT_ROLL_CLAUSE_STATES.join("|")})$`
);

/**
 * The counts a stack preview reports, in `DRIFT_PENDING_OPS` order, with every
 * zero dropped. An empty result means nothing is pending.
 */
export function driftPendingCounts(
  counts: Readonly<Partial<Record<DriftPendingOp, number>>>
): DriftPendingCount[] {
  return DRIFT_PENDING_OPS.flatMap((op) => {
    const count = counts[op] ?? 0;
    return count > 0 ? [{ count, op }] : [];
  });
}

/** `N to <op>[, N to <op>]*` — a stack's counts without its stack name. */
export function formatDriftPendingCounts(counts: readonly DriftPendingCount[]): string {
  return counts.map(({ count, op }) => `${count} to ${op}`).join(COUNT_SEPARATOR);
}

/** One clause, spelled the only way any reader accepts. */
export function formatDriftCountsClause(clause: DriftCountsClause): string {
  switch (clause.kind) {
    case "stack-counts":
      return `${clause.stack}: ${formatDriftPendingCounts(clause.counts)}`;
    case "missing-stacks":
      return `${MISSING_STACKS_PREFIX}${clause.stacks.join(COUNT_SEPARATOR)}`;
    case "raft-roll":
      return `${clause.pool}${RAFT_ROLL_INFIX}${clause.state}`;
  }
}

/** The whole counts line. */
export function formatDriftCountsLine(clauses: readonly DriftCountsClause[]): string {
  return clauses.map(formatDriftCountsClause).join(DRIFT_COUNTS_CLAUSE_SEPARATOR);
}

function isDriftPendingOp(value: string): value is DriftPendingOp {
  return (DRIFT_PENDING_OPS as readonly string[]).includes(value);
}

function isRaftRollClauseState(value: string): value is RaftRollClauseState {
  return (RAFT_ROLL_CLAUSE_STATES as readonly string[]).includes(value);
}

function parsePendingCount(phrase: string): DriftPendingCount | null {
  const match = PENDING_COUNT.exec(phrase);
  const count = match?.[1];
  const op = match?.[2];
  if (count === undefined || op === undefined || !isDriftPendingOp(op)) return null;
  return { count: Number(count), op };
}

/**
 * Read one clause back, or `null` when it is not a clause this vocabulary
 * spells — a converge fault, a reason written by some other producer, or a
 * clause a newer producer added. `null` is never a guess: a reader that cannot
 * read a clause must not call it routine.
 */
export function parseDriftCountsClause(text: string): DriftCountsClause | null {
  const roll = RAFT_ROLL_CLAUSE.exec(text);
  if (roll !== null) {
    const pool = roll[1];
    const state = roll[2];
    if (pool === undefined || state === undefined || !isRaftRollClauseState(state)) return null;
    return { kind: "raft-roll", pool, state };
  }

  const missing = MISSING_STACKS_CLAUSE.exec(text);
  if (missing !== null) {
    const stacks = missing[1];
    if (stacks === undefined) return null;
    return { kind: "missing-stacks", stacks: stacks.split(COUNT_SEPARATOR) };
  }

  const stackCounts = STACK_COUNTS_CLAUSE.exec(text);
  const stack = stackCounts?.[1];
  const phrases = stackCounts?.[2];
  if (stack === undefined || phrases === undefined) return null;
  const counts: DriftPendingCount[] = [];
  for (const phrase of phrases.split(COUNT_SEPARATOR)) {
    const count = parsePendingCount(phrase);
    if (count === null) return null;
    counts.push(count);
  }
  return { kind: "stack-counts", stack, counts };
}

/**
 * Whether one clause, on its own, is something the reconcile loop applies with
 * nobody acting. Exhaustive: a clause kind added to the union does not compile
 * here until it is given an answer.
 */
export function isRoutineDriftCountsClause(clause: DriftCountsClause): boolean {
  switch (clause.kind) {
    case "stack-counts":
      return clause.counts.length > 0 && clause.counts.every(({ op }) => ROUTINE_PENDING_OP[op]);
    // A stack that should exist and is gone is a fault, never a pending diff.
    case "missing-stacks":
      return false;
    case "raft-roll":
      return clause.state === "pending";
  }
}

/**
 * Whether a counts line is provably nothing but routine pending changes.
 *
 * Total or nothing. Every clause must parse AND be routine; one unreadable
 * clause makes the whole answer false, because this is the only reading that
 * ever takes the caution chip away and it must not accept a partial reading.
 */
export function isRoutineDriftCountsLine(countsLine: string): boolean {
  if (countsLine.length === 0) return false;
  return countsLine.split(DRIFT_COUNTS_CLAUSE_SEPARATOR).every((text) => {
    const clause = parseDriftCountsClause(text);
    return clause !== null && isRoutineDriftCountsClause(clause);
  });
}
